import { useState, useEffect, useCallback } from 'react';
import { getConfig } from '../utils/config';

// Document collection uses api-private
const API_URL = getConfig('VITE_API_URL') || 'http://localhost:7073';

// ==================== Interfaces ====================

/**
 * Deal information returned from GET /api/document-collection/deal/{dealId}
 */
export interface DealInfo {
  dealId: string;
  dealName: string;
  managingCompany: string | null;
  propertyName: string;
  clientFilesFolderId: string | null;
}

/**
 * Upload response from POST /api/document-collection/upload
 */
export interface UploadResponse {
  uploadId: string;
  sessionId: string;
  googleDriveFileId: string;
  fileName: string;
}

/**
 * Complete session response from POST /api/document-collection/complete
 */
export interface CompleteSessionResponse {
  sessionId: string;
  documentsUploaded: number;
  completedAt: string;
}

/**
 * Contact information for completing a session
 */
export interface ContactInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
}

// ==================== API Response Types ====================

interface ApiSuccessResponse<T> {
  success: true;
  data: T;
}

interface ApiErrorResponse {
  success: false;
  error: string;
  message: string;
  errors?: Record<string, string>;
}

type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse;

// ==================== Hooks ====================

/**
 * Hook for fetching deal information
 *
 * @param dealId - HubSpot Deal ID
 * @returns { dealInfo, loading, error }
 *
 * @example
 * const { dealInfo, loading, error } = useDealInfo('12345678');
 */
export const useDealInfo = (dealId: string) => {
  const [dealInfo, setDealInfo] = useState<DealInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDealInfo = async () => {
      if (!dealId) {
        setLoading(false);
        setError('Deal ID is required');
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await fetch(
          `${API_URL}/api/document-collection/deal/${encodeURIComponent(dealId)}`
        );

        const data: ApiResponse<DealInfo> = await response.json();

        if (!response.ok || !data.success) {
          const errorData = data as ApiErrorResponse;
          const errorMessage = errorData.message || 'Failed to fetch deal information';
          throw new Error(errorMessage);
        }

        setDealInfo(data.data);
        setError(null);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch deal information';
        setError(errorMessage);
        setDealInfo(null);
        console.error('Error fetching deal info:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDealInfo();
  }, [dealId]);

  return { dealInfo, loading, error };
};

/**
 * Hook for uploading documents
 *
 * @returns { uploadFile, uploading, error }
 *
 * @example
 * const { uploadFile, uploading, error } = useDocumentUpload();
 *
 * const handleUpload = async () => {
 *   const result = await uploadFile('12345', 'session-uuid', 'income_expense_2024', file);
 *   if (result) {
 *     console.log('Uploaded:', result.fileName);
 *   }
 * };
 */
export const useDocumentUpload = () => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadFile = useCallback(
    async (
      dealId: string,
      sessionId: string | null,
      documentType: string,
      file: File,
      formType?: 'standard' | 'retrospective'
    ): Promise<UploadResponse | null> => {
      try {
        setUploading(true);
        setError(null);

        // Build multipart form data
        const formData = new FormData();
        formData.append('dealId', dealId);
        if (sessionId) {
          formData.append('sessionId', sessionId);
        }
        formData.append('documentType', documentType);
        if (formType) {
          formData.append('formType', formType);
        }
        formData.append('file', file);

        const response = await fetch(`${API_URL}/api/document-collection/upload`, {
          method: 'POST',
          body: formData,
          // Note: Do not set Content-Type header - browser will set it with boundary
        });

        const data: ApiResponse<UploadResponse> = await response.json();

        if (!response.ok || !data.success) {
          const errorData = data as ApiErrorResponse;
          const errorMessage = errorData.message || 'Failed to upload document';
          throw new Error(errorMessage);
        }

        return data.data;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to upload document';
        setError(errorMessage);
        console.error('Error uploading document:', err);
        return null;
      } finally {
        setUploading(false);
      }
    },
    []
  );

  return { uploadFile, uploading, error };
};

/**
 * Hook for completing a document collection session
 *
 * @returns { completeSession, submitting, error, success }
 *
 * @example
 * const { completeSession, submitting, error, success } = useCompleteSession();
 *
 * const handleComplete = async () => {
 *   const result = await completeSession('session-uuid', {
 *     firstName: 'John',
 *     lastName: 'Doe',
 *     email: 'john@example.com',
 *   });
 *   if (result) {
 *     console.log('Session completed:', result.documentsUploaded);
 *   }
 * };
 */
export const useCompleteSession = () => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const completeSession = useCallback(
    async (
      sessionId: string,
      contactInfo: ContactInfo
    ): Promise<CompleteSessionResponse | null> => {
      try {
        setSubmitting(true);
        setError(null);
        setSuccess(false);

        const response = await fetch(`${API_URL}/api/document-collection/complete`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sessionId,
            contact: contactInfo,
          }),
        });

        const data: ApiResponse<CompleteSessionResponse> = await response.json();

        if (!response.ok || !data.success) {
          const errorData = data as ApiErrorResponse;
          const errorMessage = errorData.message || 'Failed to complete session';
          throw new Error(errorMessage);
        }

        setSuccess(true);
        return data.data;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to complete session';
        setError(errorMessage);
        console.error('Error completing session:', err);
        return null;
      } finally {
        setSubmitting(false);
      }
    },
    []
  );

  return { completeSession, submitting, error, success };
};
